import { Building, TrendingUp, Construction, TreePine, Network, Sparkles, ArrowRight, Quote } from "lucide-react";

/**
 * Strategic Alignment with the City of Baytown's adopted Strategic Plan.
 * Source: baytown.org/1041 (Strategic Plan) + Mayor Charles Johnson's
 * 2026 State of the City remarks.
 */
const alignmentRows = [
  {
    icon: TrendingUp,
    cityPriority: "Economic Prosperity",
    bridgeContribution:
      "Bridge produces a credentialed local workforce — Lee College graduates moving into living-wage industrial, medical, and logistics careers. Family wealth grows in Baytown, not commutes out of it.",
    strength: "Direct",
  },
  {
    icon: Network,
    cityPriority: "Building a Connected Baytown",
    bridgeContribution:
      "The Bridge Model is connection. One trained Service Connector per family, linking every service line — Lee College, Harris Health, Workforce Solutions, Housing Authority, AAA. Same language the City has adopted as a strategic pillar.",
    strength: "Direct · One-to-One",
    highlight: true,
  },
  {
    icon: Construction,
    cityPriority: "Critical Infrastructure & Traffic Flow",
    bridgeContribution:
      "Bridge coordinates Harris County Transit access for families navigating fragmented commutes between Lee College, Workforce Solutions, and Harris Health. Senior home modifications protect aging-in-place infrastructure inside existing housing stock.",
    strength: "Adjacent",
  },
  {
    icon: TreePine,
    cityPriority: "Outdoor Recreation & Amenities",
    bridgeContribution:
      "Stabilized, rooted Baytown families are the long-term audience for the regional park and recreation investments. Bridge graduates stay in Baytown, raise their families here, and use the amenities the City is building.",
    strength: "Adjacent",
  },
  {
    icon: Sparkles,
    cityPriority: "Beautification & Environmental Stewardship",
    bridgeContribution:
      "Senior home repair, code-compliant aging-in-place modifications, and homeownership-ladder graduates investing in older neighborhood housing stock all directly improve the visual and structural quality of Baytown's existing neighborhoods.",
    strength: "Adjacent",
  },
];

const stateOfTheCity = [
  "Market Street & West Baytown drainage improvements",
  "Sidewalks in Country Club View · street reconstruction in Lantern Park",
  "New Fire Station 2 building",
  "$151M general obligation bond package · roads + possible regional park",
  "CCPD & FCPMD tax renewals for police & fire equipment",
];

export default function StrategicAlignment() {
  return (
    <section
      id="alignment"
      data-testid="section-strategic-alignment"
      className="bg-[#F9F7F1] py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                Strategic Alignment with the City of Baytown
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              We don't compete with the City's plan.<br />
              <em className="italic">We make it possible.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              The Baytown Bridge is mapped directly to{" "}
              <strong className="text-[#0A192F]">Mayor Charles Johnson</strong>'s
              adopted Strategic Plan — and to City Manager{" "}
              <strong className="text-[#0A192F]">Jason Reynolds</strong>'s
              implementation portfolio. The City's #4 pillar — <em>Building a
              Connected Baytown</em> — is the Bridge Model expressed in the
              City's own language.
            </p>
          </div>
        </div>

        {/* Alignment table */}
        <div className="bg-white border border-[#CBD5E1] mb-10">
          <div className="grid grid-cols-12 gap-px bg-[#CBD5E1] border-b border-[#CBD5E1]">
            <div className="col-span-4 bg-[#0A192F] text-[#F9F7F1] px-5 py-3 font-mono text-[10px] uppercase tracking-widest">
              City of Baytown Priority
            </div>
            <div className="col-span-6 bg-[#0A192F] text-[#F9F7F1] px-5 py-3 font-mono text-[10px] uppercase tracking-widest">
              Bridge Model Contribution
            </div>
            <div className="col-span-2 bg-[#0A192F] text-[#F9F7F1] px-5 py-3 font-mono text-[10px] uppercase tracking-widest">
              Strength
            </div>
          </div>
          <ul>
            {alignmentRows.map((r, i) => {
              const Icon = r.icon;
              return (
                <li
                  key={r.cityPriority}
                  data-testid={`alignment-row-${i}`}
                  className={`grid grid-cols-12 border-b border-[#E2E8F0] last:border-b-0 ${
                    r.highlight ? "bg-[#FBF6E5]" : "bg-white"
                  }`}
                >
                  <div className="col-span-4 px-5 py-5 flex items-start gap-3">
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${r.highlight ? "text-[#B8860B]" : "text-[#8B0000]"}`} />
                    <div className="font-display text-base text-[#0A192F] leading-snug">
                      {r.cityPriority}
                    </div>
                  </div>
                  <div className="col-span-6 px-5 py-5 text-sm text-[#475569] leading-relaxed">
                    {r.bridgeContribution}
                  </div>
                  <div className="col-span-2 px-5 py-5">
                    <span
                      className={`inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-1 ${
                        r.highlight
                          ? "bg-[#B8860B] text-[#FDF8EF]"
                          : r.strength === "Direct"
                          ? "bg-[#0A192F] text-[#F9F7F1]"
                          : "bg-[#E2E8F0] text-[#475569]"
                      }`}
                    >
                      {r.strength}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Workforce angle callout */}
        <div
          data-testid="alignment-workforce-callout"
          className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-10 mb-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
          <div className="relative z-10 grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">
                The Workforce Behind a Connected Baytown
              </div>
              <h3 className="font-display text-2xl lg:text-3xl leading-snug mb-4">
                Every new road, fire station, park, and neighborhood requires the people to build it, staff it, and use it.
              </h3>
              <p className="text-[#CBD5E1] leading-relaxed text-sm lg:text-base">
                The $151M general-obligation bond package the City passed in 2025
                will fund roads, sidewalks, traffic improvements, drainage, and a
                possible regional park. Those projects need construction trades,
                heavy equipment operators, electricians, civil engineers,
                landscape and parks-maintenance staff, and the families to live
                in and use what the City builds.
              </p>
              <p className="text-[#CBD5E1] leading-relaxed text-sm lg:text-base mt-3">
                <strong className="text-[#F9F7F1]">
                  The Baytown Bridge produces that workforce.
                </strong>{" "}
                From Baytown residents. Through Lee College. Without importing
                from outside the city. That is the human-capital companion piece
                to the bond-funded infrastructure agenda.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-[#F9F7F1]/5 border border-[#F9F7F1]/15 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-3">
                  Mayor Johnson · 2026 State of the City
                </div>
                <ul className="space-y-2">
                  {stateOfTheCity.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-xs text-[#CBD5E1] leading-relaxed">
                      <span className="text-[#D4AF37] shrink-0 mt-0.5">▸</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-[#94A3B8] italic mt-4 leading-relaxed">
                  Bridge does not replace any of these initiatives. Bridge
                  makes them work for Baytown families.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}